import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CustomSnackbarComponent, CustomSnackbarData, SnackbarPanelClasses } from '../components/global/custom-snackbar/custom-snackbar.component';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { ConfirmationDialogData, ConfirmationDialogType, ConfirmationPopupComponent } from '../components/global/confirmation-popup/confirmation-popup.component';
import { Observable } from 'rxjs/internal/Observable';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

export enum PopupIconIDs {
  SUCCESS = 'check_circle_outline',
  INFO = 'info_outline',
  WARNING = 'report_problem_outline',
  ALERT = 'error_outline'
}

export enum SnackbarIconIDs {
  SUCCESS = 'check_circle_outline',
  INFO = 'info_outline',
  WARNING = 'report_problem_outline',
  ALERT = 'error_outline'
}

enum SnackbarTypes {
  ALERT,
  SUCCESS,
  INFO,
  WARNING
}

interface MinSnackbarConfig {
  data: CustomSnackbarData,
  isAddressListSnackbar?: boolean // true if snackbar appears in address list (adds bottom margin)
  panelClasses?: string[], // optional panel classes
  duration?: number, // Set an auto-dismiss timer in case 'Close' button should be hidden
}

interface SnackbarConfig extends MinSnackbarConfig {
  type: SnackbarTypes; // determines styling
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  constructor(public dialog: MatDialog,
              private snackbar: MatSnackBar,
              private translate: TranslateService,
              private zone: NgZone) {
  }

  /**
   * Shows a snackbar that can additionally trigger a Popup if the 'data' object includes 'additionalInfo'
   * @param config
   * @returns MatSnackBarRef<CustomSnackbarComponent> in order to close the snackbar programmatically
   */
  private showSnackbar(config: SnackbarConfig): MatSnackBarRef<CustomSnackbarComponent> | undefined {
    let panelClasses: string[] = [];

    if (config.isAddressListSnackbar) {
      panelClasses.push('address-list-snackbar');
    }

    switch (config.type) {
      case SnackbarTypes.SUCCESS:
        panelClasses.push(SnackbarPanelClasses.SUCCESS);
        break;
      case SnackbarTypes.ALERT:
        panelClasses.push(SnackbarPanelClasses.ALERT);
        break;
      case SnackbarTypes.INFO:
        panelClasses.push(SnackbarPanelClasses.INFO);
        break;
      case SnackbarTypes.WARNING:
        panelClasses.push(SnackbarPanelClasses.WARNING);
        break;
      default:
        return;
    }

    if (config?.panelClasses?.length) {
      panelClasses = panelClasses.concat(config.panelClasses);
    }

    const ref = this.snackbar.openFromComponent(CustomSnackbarComponent, {
      panelClass: panelClasses,
      duration: config.duration,
      data: config.data,
    });

    ref.afterDismissed().subscribe((val) => {
      if (val.dismissedByAction && config.data.selectedAction) {
        config.data.selectedAction();
      } else if (!val.dismissedByAction && config.data.dismissButton?.action) {
        config.data.dismissButton.action();
      }
    });

    return ref;
  }

  /**
   * Shows an error/alert snackbar (red) that can additionally trigger a Popup if the 'data' object includes
   * 'additionalInfo'. This snackbar will not close automatically, except actively set in config.
   * @param config
   * @param errorStatusCode Error status-code. Used to distinguish between client/server-errors.
   * @returns MatSnackBarRef<CustomSnackbarComponent> in order to close the snackbar programmatically
   */
  showAlertSnackbar(config: MinSnackbarConfig, errorStatusCode: number): MatSnackBarRef<CustomSnackbarComponent> | undefined {
    if (errorStatusCode >= 500) {
      config.data.detailedInfoFromBE = null;  // server errors do not return helpful error messages
    }
    return this.showSnackbar({ ...config, type: SnackbarTypes.ALERT });
  }

  /**
   * Show an alert snackbar with a retry button that will emit the next value of the provided
   * Subject => Retry = 'true' / Dismiss = 'false'
   * @use When the retry button is supposed to dispatch an NGRX action and the snackbar was
   * triggered from within an NGRX effect, this will not work (cannot dispatch an action from an
   * effect).
   * this method is a workaround - it is possible to pipe a switchMap onto a Subject to return
   * of(desiredAction) and have the NGRX effect return the resulting observable.
   * An unfortunate side effect is that if loading indicators are awaiting success/fail actions,
   * they'll keep being active until the snackbar is active
   * @param config
   */
  showAlertSnackbarWithRetryFromEffect(config: {
    err: HttpErrorResponse,
    userChoice: Subject<boolean>,
    userFriendlyMessage: string,
    hideRetryButton?: boolean
    duration?: number,
    retryButtonText?: string }): MatSnackBarRef<CustomSnackbarComponent> | undefined {

    const minSnackbarConfig: MinSnackbarConfig = {
      data: {
        userFriendlyMessage: config.userFriendlyMessage,
        detailedInfoFromBE: config.err?.error?.message ? [config.err.error.message] : null,
        // allow retry only if server error or if not explicitly forbidden
        actionButtons: config.err?.status >= 500 && !config.hideRetryButton ? [{
          text: config.retryButtonText ? config.retryButtonText : 'Retry',
          action: () => {
            // CD is not triggered by an observable emitting from an error handler, so force it
            // https://github.com/angular/angular/issues/17772
            this.zone.runTask(() => config.userChoice.next(true));
          },
        }] : null,
        dismissButton: {
          // same as above
          action: () => {
            this.zone.runTask(() => config.userChoice.next(false));
          },
        },
      },
      duration: config.duration
    }

    return this.showAlertSnackbar(minSnackbarConfig, config.err?.status);
  }

  /**
   * Shows a warning snackbar (yellow) that can additionally trigger a Popup if the 'data' object includes
   * 'additionalInfo'. This snackbar will not close automatically, except actively set in config.
   * @param config
   * @returns MatSnackBarRef<CustomSnackbarComponent> in order to close the snackbar programmatically
   */
  showWarningSnackbar(config: MinSnackbarConfig): MatSnackBarRef<CustomSnackbarComponent> | undefined {
    return this.showSnackbar({ ...config, type: SnackbarTypes.WARNING });
  }

  /**
   * Shows an info snackbar (blue) that can additionally trigger a Popup if the 'data' object includes
   * 'additionalInfo'. This snackbar will not close automatically, except actively set in config.
   * @param config
   * @returns MatSnackBarRef<CustomSnackbarComponent> in order to close the snackbar programmatically
   */
  showInfoSnackbar(config: MinSnackbarConfig): MatSnackBarRef<CustomSnackbarComponent> | undefined {
    return this.showSnackbar({ ...config, type: SnackbarTypes.INFO });
  }

  /**
   * Shows a success snackbar (green) that can additionally trigger a Popup if the 'data' object
   * includes 'additionalInfo'. The snackbar will close automatically after 3 seconds (except if the
   * duration has been actively overwritten; when set to null it won't close automatically anymore).
   * @param config
   * @returns MatSnackBarRef<CustomSnackbarComponent> in order to close the snackbar programmatically
   */
  showSuccessSnackbar(config: MinSnackbarConfig): MatSnackBarRef<CustomSnackbarComponent> | undefined {
    // auto-close success snackbars after 3s if not specified otherwise
    // check specifically for undefined here (bc if set to null, the snackbar will stay open)
    if (typeof config.duration === 'undefined') {
      config.duration = 3000;
    }

    return this.showSnackbar({ ...config, type: SnackbarTypes.SUCCESS });
  }

  /**
   * Shows a confirmation popup and returns an Observable that will emit a bool value after the
   * popup is closed.
   * @param config
   * @returns Observable<boolean>
   */
  showConfirmationPopup(config: MatDialogConfig<ConfirmationDialogData>): Observable<boolean> {
    if (!config?.data?.confirmationDialogType) {
      config.data.confirmationDialogType = ConfirmationDialogType.WARNING;
      config.data.icon = {
        name: 'delete_outline',
        iconClass: 'icon-delete',
      };
    }

    return this.dialog.open(ConfirmationPopupComponent, config).afterClosed();
  }

}
